import * as THREE from 'three';
import { PORTAL_HALF_W, PORTAL_HALF_H } from '../portals/Portal.js';

const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _axis = new THREE.Vector3();

/**
 * Local executor for the rival.
 *
 * The split matters: this file owns everything that must run at 60fps and must
 * never fail (steering, aiming, stepping into a portal), and it owns a
 * heuristic planner good enough to finish a level on its own. The LLM brain
 * only ever supplies a goal list. That way a network hiccup costs the rival
 * some cleverness, not its ability to move.
 */

/** A portal-sized face of a portalable box, in world space. */
function faceSurfaces(collider, out) {
  const he = [collider.halfExtents.x, collider.halfExtents.y, collider.halfExtents.z];
  for (let axis = 0; axis < 3; axis++) {
    const u = (axis + 1) % 3;
    const v = (axis + 2) % 3;
    // A face can hold a portal either way up, so the test is against the
    // smaller of the two portal dimensions on each tangent.
    const fits = (he[u] >= PORTAL_HALF_W && he[v] >= PORTAL_HALF_H)
              || (he[u] >= PORTAL_HALF_H && he[v] >= PORTAL_HALF_W);
    if (!fits) continue;
    for (const sign of [1, -1]) {
      const local = new THREE.Vector3();
      local.setComponent(axis, sign * he[axis]);
      const center = local.clone().applyMatrix4(collider.matrix);
      const normal = collider.axis(axis, new THREE.Vector3()).multiplyScalar(sign);
      const kind = normal.y > 0.7 ? 'floor' : normal.y < -0.7 ? 'ceiling' : 'wall';
      out.push({
        id: `s${collider.id}_${axis}${sign > 0 ? 'p' : 'n'}`,
        collider,
        axis,
        sign,
        center,
        normal,
        kind,
        halfU: he[u],
        halfV: he[v],
      });
    }
  }
  return out;
}

const SKILLS = {
  1: { aimDelay: 2.2, reactDelay: 1.5, sprint: false },   // EASY
  2: { aimDelay: 0.8, reactDelay: 0.6, sprint: true },    // NORMAL
  3: { aimDelay: 0, reactDelay: 0, sprint: true },        // BRUTAL
};

export class RivalAgent {
  constructor({ rival, world, bus }) {
    this.rival = rival;
    this.world = world;
    this.bus = bus;

    this.surfaces = [];
    this.targets = [];
    this.goals = [];
    this.current = null;
    this.phase = null;
    this._phaseTime = 0;
    this._stuckTime = 0;
    this._lastPos = new THREE.Vector3();
    this._retries = 0;
    this.lastFailure = null;
    this.input = { moveX: 0, moveZ: 0, jump: false, jumpPressed: false, sprint: true };

    // Difficulty is honesty about reaction time, not worse pathfinding: a
    // handicapped rival pauses to "think" between goals, takes its time
    // lining up portals, and jogs instead of sprinting. Route quality is
    // identical on every tier, so EASY still LOOKS like it knows the level.
    this.skill = SKILLS[3];
    this._reactLeft = 0;

    // A plan that arrives while a goal is executing waits here for the goal
    // boundary instead of interrupting. Measured live before this existed:
    // nine of ten LLM plans landed mid-move, wiping a fling in progress and
    // restarting it from scratch, forever.
    this.pendingPlan = null;
    this._pendingAt = 0;

    // Entry surfaces that keep failing get benched. The failure the player
    // sees otherwise is the rival re-firing the same doomed portal in a loop.
    this.transits = [];
    this._lastTransit = null;
    this._benched = new Map();   // surface id -> { fails, until }

    // Competing means the distance to the objective shrinks. When it stops
    // shrinking for too long, whatever we are doing is not working.
    this._objective = null;
    this._bestDist = Infinity;
    this._lastProgress = 0;
    this._clock = 0;

    // A baked route (RivalRoutes.js), consumed one step at a time. The
    // executor is the same; the route just outranks the heuristic on the
    // question of WHERE next.
    this.route = null;
    this._routeIx = 0;
    this._routeFails = 0;
    this._routeDetour = false;

    // Targets that proved unreachable from here get a cooldown, and two
    // consecutive no-progress verdicts from the same spot mean give up and
    // start over (the game respawns us). Both exist because the alternative
    // was measured: a rival parked under a floating shard for 150 seconds,
    // or humping a pit wall for the rest of the race.
    this._cold = new Map();      // "x,y,z" -> until (clock seconds)
    this._stallPos = null;
    this._strandedOut = false;
  }

  /** Rebuild the affordance list. Cheap enough to redo on every level load. */
  survey(level) {
    this.surfaces.length = 0;
    for (const c of this.world.colliders) {
      if (!c.portalable || c.hasTag('portal-filler')) continue;
      faceSurfaces(c, this.surfaces);
    }

    this.transits = [];
    for (const t of level.triggers) {
      if (t.anyBody) this.transits.push({ position: t.center.clone(), half: t.half.clone() });
    }

    this.targets = [];
    for (const g of level.byRole.goal) {
      const t = g.triggers?.[0];
      if (t) this.targets.push({ kind: 'goal', position: t.center.clone(), entity: g });
    }
    for (const s of level.byRole.shard) {
      const t = s.triggers?.[0];
      if (t) this.targets.push({ kind: 'shard', position: t.center.clone(), entity: s, taken: false });
    }
    this.goals.length = 0;
    this.current = null;
    this.pendingPlan = null;
    this._benched.clear();
    this.lastFailure = null;
    this._lastTransit = null;
    this._reactLeft = 0;
    this._objective = null;
    this._bestDist = Infinity;
    this._lastProgress = this._clock;
    this._cold.clear();
    this._stallPos = null;
    this._strandedOut = false;
  }

  /** Install (or clear) the level's baked route. Call after survey(). */
  setRoute(steps) {
    this.route = Array.isArray(steps) && steps.length ? steps : null;
    this._routeIx = 0;
    this._routeFails = 0;
    this._routeDetour = false;
    this.branch = null;
    this._branchTaken = null;
  }

  /**
   * A branch step offers two lines to the same place: a safe one and a
   * fast one. The executor will not choose (it has no idea whether the
   * race is close); it publishes the choice and defaults to safe. The
   * brain answers with pick(), which is the one live decision an LLM is
   * actually good at here: not steering, judging.
   */
  pick(which) {
    if (!this.branch) return false;
    this._branchTaken = which === 'fast' ? 'fast' : 'safe';
    return true;
  }

  get routeActive() { return !!this.route && this._routeIx < this.route.length; }

  /**
   * Death restarts the script from the top. That is the honest semantic on
   * every level: shard-tagged steps skip themselves once collected, so a
   * collect level fast-forwards to where it left off, and a course level
   * (no rival checkpoints) genuinely starts over.
   */
  routeReset() {
    this._routeIx = 0;
    this._routeFails = 0;
    this._routeDetour = false;
  }

  /**
   * True once when the agent has concluded it cannot move from here: two
   * consecutive stall verdicts without displacement. The game treats it as a
   * death (respawn), which is exactly what a stranded human does.
   */
  takeStranded() {
    if (!this._strandedOut) return false;
    this._strandedOut = false;
    this._stallPos = null;
    return true;
  }

  markShardTaken(position) {
    for (const t of this.targets) {
      if (t.kind === 'shard' && t.position.distanceToSquared(position) < 4) t.taken = true;
    }
  }

  /**
   * Accept a plan. Unless forced, a plan never interrupts the goal being
   * executed: it queues for the next boundary, and goes stale after ten
   * seconds because it was written against a world that has moved on.
   */
  setPlan(goals, { force = false } = {}) {
    if (!Array.isArray(goals) || goals.length === 0) return false;
    // Advice that is nothing but waiting is not advice, it is camping.
    // (Forced plans are exempt: the death interrupt is a short wait.)
    if (!force && goals.every((g) => g?.kind === 'wait')) return false;
    const cleaned = this._normalise(goals.slice(0, 6));
    if (!cleaned.length) return false;

    if (force || !this.current) {
      this.goals = cleaned;
      this.current = null;
      this.pendingPlan = null;
    } else {
      this.pendingPlan = cleaned;
      this._pendingAt = this._clock;
    }
    return true;
  }

  /**
   * The label a planner puts on a portal move is advice; the entry surface's
   * geometry is fact. A "fling" whose entry is a wall gets window portals
   * placed at face centre and the body can never enter them; the same pair
   * executed as a walk opens doors at foot level and works. Trust geometry.
   */
  _normalise(goals) {
    const out = [];
    for (const g of goals) {
      if (g.kind === 'wait') {
        // Advice may pause, not camp: measured junk plans were six waits
        // in a row while the race ran on.
        out.push({ ...g, seconds: Math.min(1.5, Number(g.seconds) || 1) });
        continue;
      }
      if (g.kind !== 'fling' && g.kind !== 'walk') { out.push(g); continue; }
      const from = this.surfaceById(g.from);
      const to = this.surfaceById(g.to);
      if (!from || !to) continue;
      if (this._isBenched(g.from)) continue;
      const kind = from.kind === 'floor' ? 'fling' : 'walk';
      out.push({ ...g, kind });
    }
    return out;
  }

  _isBenched(id) {
    const b = this._benched.get(id);
    return !!b && b.until > this._clock;
  }

  _bench(id, strikes = 1) {
    const b = this._benched.get(id) ?? { fails: 0, until: 0 };
    b.fails += strikes;
    if (b.fails >= 2) b.until = this._clock + 25;
    this._benched.set(id, b);
  }

  setSkill(level) {
    this.skill = SKILLS[level] ?? SKILLS[3];
    this.input.sprint = this.skill.sprint;
  }

  get idle() { return !this.current && this.goals.length === 0; }

  // -------------------------------------------------------- heuristic plan

  /** The offline planner. Always produces something executable. */
  plan() {
    if (this.routeActive) {
      // Two straight failures on a step buy the heuristic one recovery leg
      // (a detour), after which the route resumes at whatever step the world
      // now says is next. The route is knowledge, not a rail.
      if (this._routeDetour) {
        this._routeDetour = false;
      } else {
        const g = this._routeGoal();
        if (g) return g;
      }
    }
    const target = this._nextTarget();
    if (!target) return [{ kind: 'wait', seconds: 1 }];
    this._setObjective(target.position);

    const pos = this.rival.position;
    const dy = target.position.y - pos.y;
    const flat = Math.hypot(target.position.x - pos.x, target.position.z - pos.z);

    // Close and roughly level: just run at it.
    if (flat < 26 && dy > -6 && dy < 3.5) {
      return [{ kind: 'goto', target: target.position.toArray() }];
    }

    const fling = this._chooseFling(target.position);
    if (fling) {
      return [
        { kind: 'fling', from: fling.from.id, to: fling.to.id },
        { kind: 'goto', target: target.position.toArray() },
      ];
    }

    // No drop to convert: cross the gap on foot through a wall pair. This is
    // the game's most basic move and the one BOOT is built around; a rival
    // that only knows flings just sprints into the gap and dies there.
    const walkPair = this._chooseWalk(target.position);
    if (walkPair) {
      return [
        {
          kind: 'walk',
          from: walkPair.from.id,
          to: walkPair.to.id,
          exitY: target.position.y,
        },
        { kind: 'goto', target: target.position.toArray() },
      ];
    }

    // No portal route: ride the level's own machinery. Pads and lifts are
    // anyBody triggers now, so stepping into one launches the rival exactly
    // as it launches the player; the replan after the ride sees a fresh
    // board from wherever the machinery dropped us.
    const transit = this._chooseTransit(target.position);
    if (transit) {
      this._lastTransit = { pos: _v2.clone().copy(this._transitPos), at: this._clock };
      return [
        ...transit.map((t) => ({ kind: 'goto', target: t.target, minY: t.minY })),
        { kind: 'wait', seconds: 0.4 },
      ];
    }

    // No fling reachable and the target is far: stranded, most likely at the
    // bottom of a pit whose only exit is our own portal. Walking back into it
    // moves us somewhere else, and the replan after the transit sees a fresh
    // board. An escape ladder, not a route.
    const linked = this.rival.portals.isLinked('rival');
    if (linked && (flat > 30 || Math.abs(dy) > 10)) {
      const mouth = this.rival.portals.portal('rival', 'a').group.position;
      return [
        { kind: 'goto', target: [mouth.x, mouth.y, mouth.z] },
        { kind: 'wait', seconds: 0.4 },
      ];
    }
    return [{ kind: 'goto', target: target.position.toArray() }];
  }

  /**
   * The next executable route step. Resume logic lives here rather than in
   * a death handler: steps whose shard is already taken are skipped, and a
   * missing portal link rewinds to the step that places the pair, so a
   * death, a detour, or a rotted portal all re-enter the script at the
   * right line without anyone tracking why.
   */
  _routeGoal() {
    while (this._routeIx < this.route.length
      && this.route[this._routeIx].shard
      && this._shardTaken(this.route[this._routeIx].shard)) this._routeIx++;
    if (this._routeIx >= this.route.length) return null;

    if (!this.rival.portals.isLinked('rival')) {
      // Rewind to the most recent step that places the pair. A dive with no
      // pair and no placer behind it can never work: drop it.
      let placer = -1;
      for (let i = 0; i <= this._routeIx && i < this.route.length; i++) {
        const k = this.route[i].kind;
        if (k === 'fling' || k === 'walk') placer = i;
      }
      if (placer >= 0) this._routeIx = placer;
      else if (this.route[this._routeIx].kind === 'dive') { this._routeIx++; return null; }
    }

    let step = this.route[this._routeIx];
    const ix = this._routeIx;

    // A branch resolves to one of its two lines, then plays that line's
    // steps in order. Unanswered, it takes the safe one after a beat:
    // the race does not pause for a planner.
    if (step.kind === 'branch') {
      if (!this.branch || this.branch.at !== ix) {
        this.branch = { at: ix, since: this._clock, why: step.why ?? '' };
        this._branchTaken = null;
      }
      const decided = this._branchTaken
        ?? (this._clock - this.branch.since > 2.5 ? 'safe' : null);
      if (!decided) return [{ kind: 'wait', seconds: 0.3, _route: ix }];
      const line = decided === 'fast' ? step.fast : step.safe;
      if (!Array.isArray(line) || !line.length) { this._routeIx++; return null; }
      // Splice the chosen line in place of the branch: from here it is an
      // ordinary stretch of route, failures and rewinds included.
      this.route = [
        ...this.route.slice(0, ix), ...line, ...this.route.slice(ix + 1),
      ];
      this.branch = null;
      this._branchTaken = null;
      this.bus?.emit('rival:branch', { took: decided, why: step.why ?? '' });
      step = this.route[ix];
      if (!step) return null;
    }
    if (step.kind === 'goto') {
      this._setObjective(_v.set(step.target[0], step.target[1], step.target[2]));
      // A goto whose target IS its shard completes only by collecting it;
      // a goto merely tagged with one (an approach hop) lands normally and
      // is skipped once that shard is gone.
      const collect = step.shard
        && Math.hypot(step.target[0] - step.shard[0], step.target[2] - step.shard[2]) < 4
        && Math.abs(step.target[1] - step.shard[1]) < 6;
      // air: the step completes at altitude (riding a lift, threading a
      // window); landing steps demand grounded-in-band.
      return [{
        kind: 'goto', target: step.target, minY: step.minY,
        shard: collect ? step.shard : undefined,
        land: step.minY != null && !step.air, launch: step.launch, _route: ix,
      }];
    }
    if (step.kind === 'dive') {
      return [{ kind: 'dive', _route: ix }];
    }
    if (step.kind === 'plunge') {
      const from = this._surfaceAt(step.entry, 'floor');
      const to = this._surfaceAt(step.exit);
      if (!from || !to) { this._routeIx++; return null; }   // the level moved
      return [{
        kind: 'plunge', from: from.id, to: to.id,
        aimFrom: step.entry, aimTo: step.exit, _route: ix,
      }];
    }
    if (step.kind === 'fling' || step.kind === 'walk') {
      const from = this._surfaceAt(step.from, step.kind === 'fling' ? 'floor' : 'wall');
      const to = this._surfaceAt(step.to);
      if (!from || !to) { this._routeIx++; return null; }   // the level moved
      return [{
        kind: step.kind, from: from.id, to: to.id,
        aimFrom: step.from, aimTo: step.to, exitY: step.exitY, _route: ix,
      }];
    }
    this._routeIx++;
    return null;
  }

  _shardTaken(p) {
    for (const t of this.targets) {
      if (t.kind === 'shard' && t.taken
        && t.position.distanceToSquared(_v.set(p[0], p[1], p[2])) < 9) return true;
    }
    return false;
  }

  /**
   * The surface a route position lies ON: close to the face plane and
   * within its extents. Route pins sit wherever the move needs them (a lip,
   * a landing line), which can be far from the face's centre, so a
   * nearest-centre lookup would reject perfectly valid pins.
   */
  _surfaceAt(p, kind = null) {
    _v.set(p[0], p[1], p[2]);
    let best = null;
    let bd = Infinity;
    for (const s of this.surfaces) {
      if (kind && s.kind !== kind) continue;
      _v2.subVectors(_v, s.center);
      const off = Math.abs(_v2.dot(s.normal));
      if (off > 2.5) continue;
      const u = (s.axis + 1) % 3;
      const v = (s.axis + 2) % 3;
      const du = Math.abs(_v2.dot(s.collider.axis(u, _axis)));
      const dv = Math.abs(_v2.dot(s.collider.axis(v, _axis)));
      if (du > s.halfU + 1 || dv > s.halfV + 1) continue;
      if (off < bd) { bd = off; best = s; }
    }
    return best;
  }

  _coldKey(p) { return `${Math.round(p.x)},${Math.round(p.y)},${Math.round(p.z)}`; }

  _nextTarget() {
    const fresh = (t) => (this._cold.get(this._coldKey(t.position)) ?? 0) <= this._clock;
    const shards = this.targets.filter((t) => t.kind === 'shard' && !t.taken && fresh(t));
    const pool = shards.length
      ? shards
      : this.targets.filter((t) => t.kind === 'goal' && fresh(t));
    if (!pool.length) return null;
    const p = this.rival.position;
    let best = null;
    let bestD = Infinity;
    for (const t of pool) {
      const d = t.position.distanceToSquared(p);
      if (d < bestD) { bestD = d; best = t; }
    }
    return best;
  }

  /**
   * Pick an entry and exit surface. The entry is whatever floor gives the
   * biggest drop from here; the exit is whichever surface points at the target.
   * That is exactly how the levels are built, so the heuristic and the level
   * design agree without either knowing about the other.
   */
  _chooseFling(targetPos) {
    const pos = this.rival.position;
    const wantHeight = targetPos.y - pos.y > 12;

    let from = null;
    let fromScore = -Infinity;
    for (const s of this.surfaces) {
      if (s.kind !== 'floor') continue;
      if (this._isBenched(s.id)) continue;
      const drop = pos.y - s.center.y;
      if (drop < 12) continue;                       // not worth the trip
      const flat = Math.hypot(s.center.x - pos.x, s.center.z - pos.z);
      // The body has to WALK to the entry. Measured on VELOCITY: without
      // this cap the chooser picks a floor 340m up the course and the
      // executor marches the rival off the deck edge trying to reach it.
      if (flat > 80) continue;
      const score = drop - flat * 0.6;
      if (score > fromScore) { fromScore = score; from = s; }
    }
    if (!from) return null;

    let to = null;
    let toScore = -Infinity;
    for (const s of this.surfaces) {
      if (s === from) continue;
      if (wantHeight) {
        // Launch straight up: exit through a floor near the target's column.
        if (s.kind !== 'floor') continue;
        // The launch reaches (entry drop) metres above the EXIT floor, so
        // any floor well above the entry works, including one below us:
        // exiting through the deck you stand beside is UPLINK's whole
        // trick. Filtering against current height instead of the entry
        // left pad tops (no line of sight to their upper face) as the
        // only candidates, and the rival aimed at them forever.
        if (s.center.y < from.center.y + 12) continue;
        const flat = Math.hypot(s.center.x - targetPos.x, s.center.z - targetPos.z);
        const score = -flat;
        if (score > toScore) { toScore = score; to = s; }
      } else {
        if (s.kind !== 'wall') continue;
        _v.subVectors(targetPos, s.center);
        _v.y = 0;
        if (_v.lengthSq() < 1) continue;
        _v.normalize();
        const aim = _v.dot(_v2.copy(s.normal).setY(0).normalize());
        if (aim < 0.55) continue;                    // points somewhere else
        const reach = 1 - Math.min(1, s.center.distanceTo(pos) / 220);
        const score = aim * 2 + reach;
        if (score > toScore) { toScore = score; to = s; }
      }
    }
    if (!to) return null;
    return { from, to };
  }

  /**
   * A wall pair for crossing on foot: entry facing me and close, exit facing
   * the target and close to it. No line-of-sight test here; the executor
   * retries while closing distance, which covers occlusion better than any
   * static check would.
   */
  _chooseWalk(targetPos) {
    const pos = this.rival.position;

    let from = null;
    let fromScore = -Infinity;
    for (const s of this.surfaces) {
      if (s.kind !== 'wall') continue;
      if (this._isBenched(s.id)) continue;
      // The face must point at us, or we would be portaling the far side.
      _v.subVectors(pos, s.center);
      if (_v.dot(s.normal) < 0.5) continue;
      const d = s.center.distanceTo(pos);
      if (d > 70) continue;
      const score = -d;
      if (score > fromScore) { fromScore = score; from = s; }
    }
    if (!from) return null;

    let to = null;
    let toScore = -Infinity;
    for (const s of this.surfaces) {
      if (s === from || s.kind !== 'wall') continue;
      _v.subVectors(targetPos, s.center);
      if (_v.dot(s.normal) < 0.5) continue;          // must open toward the target
      if (s.center.distanceTo(pos) > 200) continue;  // must be aimable from here-ish
      const d = s.center.distanceTo(targetPos);
      if (d > 55) continue;
      const score = -d;
      if (score > toScore) { toScore = score; to = s; }
    }
    if (!to) return null;
    return { from, to };
  }

  /**
   * A pad or lift worth stepping into: overhead targets want the column
   * whose base is nearest the target's column (the lift does the climbing);
   * otherwise the ride must be roughly on the way, not a scenic detour.
   */
  _chooseTransit(targetPos) {
    const pos = this.rival.position;
    const overhead = targetPos.y - pos.y > 10;
    const direct = pos.distanceTo(targetPos);
    let best = null;
    let bestScore = -Infinity;
    for (const tr of this.transits) {
      const c = tr.position;
      const dMe = pos.distanceTo(c);
      if (dMe > 60 || dMe < 2.5) continue;           // unreachable, or already inside
      // The ride we JUST took does not count: measured on VELOCITY, the
      // lift's mid-column centre scores 3D-closest to the goal from its own
      // top, so the chooser sent the rival straight back down the column,
      // forever. The course continues on the machinery you have not used.
      if (this._lastTransit && this._clock - this._lastTransit.at < 12
        && c.distanceToSquared(this._lastTransit.pos) < 9) continue;
      const via = dMe + c.distanceTo(targetPos);
      if (!overhead && via > direct * 1.25 + 8) continue;
      const score = overhead
        ? -Math.hypot(c.x - targetPos.x, c.z - targetPos.z)
        : -via;
      if (score > bestScore) { bestScore = score; best = tr; }
    }
    if (!best) return null;
    this._transitPos = best.position;
    const c = best.position;
    if (best.half.y > 4) {
      // A lift is a two-step ride: walk into the column at the base, then
      // STEP OFF at the top. When the level chains machinery (VELOCITY's
      // launch pad sits on the ring deck beside the lift top), step off
      // straight toward the next ride while still airborne: leaving the
      // field high with upward velocity clears deck gaps ballistically.
      // Traced failure without this: descend, walk toward the pad on foot,
      // fall through the ring gap, ride the lift forever.
      const topY = c.y + best.half.y;
      let next = null;
      let nd = Infinity;
      for (const tr of this.transits) {
        if (tr === best) continue;
        const d = tr.position.distanceTo(_v2.set(c.x, topY, c.z));
        if (d < 30 && d < nd) { nd = d; next = tr; }
      }
      let off;
      if (next) {
        off = [next.position.x, next.position.y - next.half.y + 0.5, next.position.z];
      } else {
        // No chained ride: aim past the column-to-deck gap, into the middle
        // of whatever is up there, in the target's direction.
        _v.set(targetPos.x - c.x, 0, targetPos.z - c.z);
        if (_v.lengthSq() < 1) _v.set(0, 0, -1);
        _v.normalize().multiplyScalar(Math.max(best.half.x, best.half.z) + 6.5);
        off = [c.x + _v.x, topY - 1, c.z + _v.z];
      }
      return [
        { target: [c.x, c.y - best.half.y + 0.5, c.z], minY: topY - 6 },
        { target: off },
      ];
    }
    return [{ target: [c.x, c.y - best.half.y + 0.5, c.z] }];
  }

  surfaceById(id) { return this.surfaces.find((s) => s.id === id) || null; }

  // ------------------------------------------------------------- execution

  update(dt) {
    const input = this.input;
    input.moveX = 0; input.moveZ = 0; input.jumpPressed = false; input.jump = false;
    if (!this.rival.enabled || this.rival.finished) return input;
    this._clock += dt;

    // The rival runs its own race on collect levels. Pickup triggers only
    // test the player, so without this it beelines the nearest shard and
    // then orbits it forever, never satisfied: standing on a shard counts
    // as collecting it for the rival's routing.
    for (const t of this.targets) {
      if (t.kind === 'shard' && !t.taken
        && t.position.distanceToSquared(this.rival.position) < 5.3) t.taken = true;
    }

    this._watchProgress();

    if (!this.current) {
      // A queued plan is taken at the boundary it waited for, and only when
      // the move that just ended FAILED. On the happy path the heuristic is
      // measurably the better router on these levels, so outside advice is
      // reserved for its designed moment: the heuristic just got it wrong,
      // do something different. It also has to still be fresh.
      if (this.pendingPlan) {
        if (this.lastFailure !== null && this._clock - this._pendingAt < 10) {
          this.goals = this.pendingPlan;
        }
        this.pendingPlan = null;
      }
      if (!this.goals.length) this.goals = this.plan();
      this.current = this.goals.shift() ?? null;
      if (this.current) this._reactLeft = this.skill.reactDelay;
      this.phase = 'start';
      this._phaseTime = 0;
      this._retries = 0;
      this._stuckTime = 0;
      this._lastPos.copy(this.rival.position);
    }
    if (!this.current) return input;
    if (this._reactLeft > 0) { this._reactLeft -= dt; return input; }

    this._phaseTime += dt;
    this._trackStuck(dt);

    switch (this.current.kind) {
      case 'goto': this._runGoto(dt); break;
      case 'fling': this._runFling(dt); break;
      case 'walk': this._runFling(dt); break;   // shared executor; kind branches inside
      case 'dive': this._runDive(dt); break;
      case 'plunge': this._runPlunge(dt); break;
      case 'wait': if (this._phaseTime > (this.current.seconds ?? 1)) this._finish(); break;
      case 'taunt': this.bus.emit('rival:say', { text: String(this.current.text ?? '') }); this._finish(); break;
      default: this._finish();
    }
    return input;
  }

  _finish(failure = null) {
    this.lastFailure = failure;
    const routeIx = this.current?._route;
    if (routeIx != null && this.route) {
      if (failure == null) {
        if (routeIx === this._routeIx) this._routeIx = routeIx + 1;
        this._routeFails = 0;
        this._lastOffIx = null;
        this._offDepth = 0;
      } else if (failure === 'off_target') {
        // Grounded off a landing (below it, or parked on a ledge above
        // it): the fix is a previous launch, not another walk in place.
        // Repeats at the SAME landing rewind one launcher further each
        // time: a launch that keeps missing may be physically short (a
        // deck-start dive against a 96m ledge), and the taller setup lives
        // further back in the script. No launcher at all: one step back,
        // so whatever machinery got us up last time gets ridden again.
        this._offDepth = routeIx === this._lastOffIx ? this._offDepth + 1 : 1;
        this._lastOffIx = routeIx;
        let want = this._offDepth;
        let back = Math.max(0, routeIx - 1);
        for (let i = routeIx - 1; i >= 0; i--) {
          const k = this.route[i].kind;
          if (k === 'dive' || k === 'plunge' || k === 'fling') {
            back = i;
            if (--want <= 0) break;
          }
        }
        this._routeIx = back;
        this._routeFails = 0;
      } else if (++this._routeFails >= 2) {
        this._routeFails = 0;
        this._routeDetour = true;
      }
    }
    if (failure && this.current && (this.current.kind === 'fling' || this.current.kind === 'walk')) {
      // During aimTo the surface that failed is the EXIT, and an exit you
      // cannot see after fourteen seconds of trying is geometry (a pad's
      // top face from below), not a fluke: bench it on the first strike.
      // Entry failures stay two-strike; approaches legitimately take time.
      const aimTo = this.phase === 'aimTo';
      this._bench(aimTo ? this.current.to : this.current.from,
        aimTo && failure === 'no_line_of_sight' ? 2 : 1);
    }
    this.current = null;
    this.phase = null;
  }

  _setObjective(pos) {
    if (!this._objective) this._objective = pos.clone();
    else if (!this._objective.equals(pos)) this._objective.copy(pos);
    else return;
    this._bestDist = this.rival.position.distanceTo(this._objective);
    this._lastProgress = this._clock;
  }

  /**
   * The one definition of competing: the distance to the objective shrinks.
   * Twelve seconds without it shrinking means the current activity, whatever
   * it thinks it is doing, has failed; scrap everything and replan fresh.
   */
  /**
   * The point the watchdog holds the rival accountable to RIGHT NOW. A
   * momentum fling legitimately moves away from the objective first (down,
   * for speed), so mid-move the yardstick is the move's own waypoint: the
   * surface being aimed at, then the portal mouth being walked into. Only
   * goto/wait/coast answer for the objective itself.
   */
  _watchPoint() {
    const cur = this.current;
    if (cur && (cur.kind === 'fling' || cur.kind === 'walk' || cur.kind === 'plunge')
      && this.phase && this.phase !== 'coast') {
      const from = this.surfaceById(cur.from);
      if (from) {
        const opening = this.rival.portals.portal('rival', 'a');
        const entering = this.phase === 'enter' || this.phase === 'fall';
        return (entering && opening?.active) ? opening.group.position : from.center;
      }
    }
    if (cur?.kind === 'dive') {
      const opening = this.rival.portals.portal('rival', 'a');
      if (opening?.active) return opening.group.position;
    }
    return this._objective;
  }

  _watchProgress() {
    // Between goals `current` is null for a frame; keying on that frame
    // reset the stall clock 60 times a second, which is how a rival
    // finishing a no-op goal every frame could stand still forever without
    // the watchdog noticing. Judge only frames that have a goal.
    if (!this.current) return;
    const point = this._watchPoint();
    if (!point) return;
    // Switching waypoints (new phase, new goal) restarts the clock; distance
    // to the new yardstick says nothing about progress toward the old one.
    const key = `${this.current.kind}:${this.current.from ?? ''}:${this.phase}`;
    if (key !== this._watchKey) {
      this._watchKey = key;
      this._bestDist = this.rival.position.distanceTo(point);
      this._lastProgress = this._clock;
      return;
    }
    const d = this.rival.position.distanceTo(point);
    if (d < this._bestDist - 1) {
      this._bestDist = d;
      this._lastProgress = this._clock;
      return;
    }
    if (this._clock - this._lastProgress > 12) {
      // No bench here: the executor's own timeouts (aim, enter) bench with
      // real evidence against a specific surface. The watchdog only knows
      // the route stalled, and benching the level's one viable fling entry
      // on that hunch is how the rival ends up with no moves at all.
      this.goals.length = 0;
      this.pendingPlan = null;
      this.current = null;
      this.phase = null;
      this.lastFailure = 'no_progress';
      this._lastProgress = this._clock;
      this._watchKey = null;
      // Two stall verdicts from the same few metres of ground mean no plan
      // from here will differ: give up and let the game respawn us. A pit
      // whose floor is above the kill plane is otherwise a life sentence.
      const p = this.rival.position;
      if (this._stallPos && this._stallPos.distanceToSquared(p) < 16) {
        this._strandedOut = true;
        this._stallPos = null;
      } else {
        this._stallPos = (this._stallPos ?? new THREE.Vector3()).copy(p);
      }
    }
  }

  _trackStuck(dt) {
    const moved = this.rival.position.distanceToSquared(this._lastPos);
    if (moved < 0.04) this._stuckTime += dt;
    else { this._stuckTime = 0; this._lastPos.copy(this.rival.position); }
  }

  /** Steer toward a world point; hop when a wall gets in the way. */
  _steerTo(point, arriveRadius = 1.6, hop = true) {
    const pos = this.rival.position;
    const dx = point.x - pos.x;
    const dz = point.z - pos.z;
    const flat = Math.hypot(dx, dz);
    if (flat < arriveRadius) return true;

    this.rival.yaw = Math.atan2(-dx, -dz);
    this.input.moveZ = 1;

    const c = this.rival.controller;
    // Blocked and on the ground: try the obvious thing before giving up.
    // Falling moves (dive, leap) damp it: their target is DOWN, and a
    // toe-stub hop at a lip turns a clean drop into a bounce festival, so
    // they jump only when genuinely wedged.
    const wedged = hop
      ? (c.collision.wall || this._stuckTime > 0.35)
      : this._stuckTime > 1.2;
    if (c.grounded && wedged) {
      this.input.jump = true;
      this.input.jumpPressed = true;
    }
    return false;
  }

  _runGoto(dt) {
    const cur = this.current;
    const t = cur.target;
    _v.set(t[0], t[1], t[2]);

    // A shard step is done when the shard is TAKEN, nothing less. Traced
    // before this existed: a 78m launch flat-passes the pad at apex, the
    // "arrival" advances the route, and the shard stays exactly where it was.
    if (cur.shard && this._shardTaken(cur.shard)) { this._finish(); return; }
    // A launch step (walk into a pad) is done when the pad FIRES: position
    // near a pad is not a launch, and the next step must begin mid-flight
    // to steer the arc.
    if (cur.launch && this.rival.controller.velocity.y > 10) { this._finish(); return; }

    const y = this.rival.position.y;
    const grounded = this.rival.controller.grounded;
    const flat = Math.hypot(t[0] - this.rival.position.x, t[2] - this.rival.position.z);
    // A landing step that finds itself grounded off the landing (back at
    // the bottom, in the pit, or parked on a ledge ABOVE the pad) is not
    // going to walk its way out: fail fast so the route can rewind to the
    // dive that sets the approach up again. The grace skips the grounded
    // frame the launch itself starts from.
    if (cur.land && grounded) {
      const below = y < cur.minY - 2 && (flat < 8 || y < cur.minY - 25);
      const above = y > cur.minY + 8 && flat < 4;
      if (below || above) {
        this._offTime = (this._offTime ?? 0) + dt;
        if (this._offTime > 0.6) { this._offTime = 0; this._finish('off_target'); return; }
      } else {
        this._offTime = 0;
      }
    }

    if (this._steerTo(_v, cur.land ? 2.6 : 1.8)) {
      // minY gates arrival vertically: riding a lift, "under the column"
      // is not "up the column". Landing steps also gate ABOVE (grounded, in
      // band): sailing over the pad at apex is not standing on it.
      const inBand = cur.minY == null
        || (cur.land
          ? (grounded && y >= cur.minY && y <= cur.minY + 8)
          : y >= cur.minY);
      if (inBand) {
        // Same for a launch step: arrival radius is wider than the pad's
        // trigger, so keep walking INTO it until it fires.
        if (cur.launch) {
          this.rival.yaw = Math.atan2(
            -(t[0] - this.rival.position.x), -(t[2] - this.rival.position.z));
          this.input.moveZ = 1;
          if (this._phaseTime > 8) this._finish('never_launched');
          return;
        }
        // A shard step never finishes on position alone; standing on the
        // pad takes the shard by touch, and THAT finishes it (above). A
        // hop covers the odd shard floating just out of reach.
        if (cur.shard) {
          if (grounded && this._phaseTime > 1.5) {
            this.input.jump = true;
            this.input.jumpPressed = true;
          }
          if (this._phaseTime > 8) this._finish('shard_unreached');
          return;
        }
        // Standing under a target well overhead is not arrival, and the
        // 150-second statue this check replaces proved it: cold the target
        // so the router tries something else for a while.
        if (cur.minY == null && _v.y - y > 3 && grounded && cur._route == null) {
          this._cold.set(this._coldKey(_v), this._clock + 25);
          this._finish('target_above');
          return;
        }
        this._finish();
      }
      return;
    }
    // A goto that cannot make progress is a planning failure, not a movement
    // one: give the brain a chance to route around it.
    if (this._stuckTime > 3.5 || this._phaseTime > 26) this._finish('goto_stuck');
  }

  /**
   * Fall back into your own entry portal until a transit registers. The
   * verb behind every repeat of a vertical-fling loop: the pair is already
   * placed, so the whole move is "walk off toward the mouth and let gravity
   * finish the argument". Intermediate landings (a deck between the pad and
   * the pit) cost nothing; the steering just keeps walking.
   */
  _runDive(dt) {
    const opening = this.rival.portals.portal('rival', 'a');
    if (!opening?.active || !this.rival.portals.isLinked('rival')) {
      this._finish('no_portals');
      return;
    }
    const c = this.rival.controller;
    if (this.phase === 'start') {
      this.phase = 'dive';
      this._travelMark = c.stats.portalTravels;
    }
    this._steerTo(opening.group.position, 0.35, false);
    if (c.stats.portalTravels !== this._travelMark) {
      this._finish();
      return;
    }
    if (this._phaseTime > 16) this._finish('dive_lost');
  }

  /**
   * The move a fling cannot make: enter through a floor you cannot see from
   * where you stand. Probed on UPLINK: from anywhere on the deck, a ray at
   * the pit floor hits the deck at your own feet (that misfired portal was
   * the "shoots a portal and stands in it" bug report). A human fires down
   * mid-fall, so the rival does too: place the EXIT first from safe ground,
   * then leap off the lip and place the entry while falling into it.
   */
  _runPlunge(dt) {
    const from = this.surfaceById(this.current.from);
    const to = this.surfaceById(this.current.to);
    if (!from || !to) { this._finish('unknown_surface'); return; }
    const c = this.rival.controller;
    const entry = this.current.aimFrom;
    const exit = this.current.aimTo;
    // Routes always pin both ends; anything else asking for a plunge
    // (a hallucinated LLM verb) fails politely instead of crashing at 60fps.
    if (!Array.isArray(entry) || !Array.isArray(exit)) { this._finish('unknown_surface'); return; }

    if (this.phase === 'start') this.phase = 'placeExit';

    // A transit only finishes the plunge when it happened AT SPEED: the
    // launch the whole move exists for. Traced on SPIRE: a gentle bounce
    // through your own exit portal (walk-in pace, both directions) also
    // increments the travel counter, and treating it as success handed the
    // next step a rival at the bottom of the shaft with no launch. Slow
    // transits just re-arm the mark; the steering keeps working the fall.
    if (this.phase === 'leap' || this.phase === 'fall') {
      if (c.stats.portalTravels !== this._travelMark) {
        this._travelMark = c.stats.portalTravels;
        if (this.rival.speed > 18) { this._finish(); return; }
      }
    }

    if (this.phase === 'placeExit') {
      if (this._phaseTime < this.skill.aimDelay) return;
      _v2.set(exit[0], exit[1], exit[2]);
      const res = this.rival.fire('b', _v2);
      if (res.ok && res.point.distanceTo(_v2) < 6) {
        this.phase = 'leap';
        this._phaseTime = 0;
        this._nextShot = 0;
        this._travelMark = c.stats.portalTravels;
        return;
      }
      this._retries++;
      _v.set(exit[0], exit[1], exit[2]).addScaledVector(to.normal, 3);
      this._steerTo(_v, 2.5);
      if (this._retries > 90 || this._phaseTime > 14 + this.skill.aimDelay) this._finish('no_line_of_sight');
      return;
    }

    if (this.phase === 'leap') {
      // Walk off toward the entry pin, then take the shot mid-fall. Firing
      // while still grounded is worse than useless: the probe showed the
      // ray hitting the deck at the rival's own feet, and a junk portal at
      // your feet is a trap you then bounce through (the exact "fires two
      // and bounces through them" bug report).
      _v.set(entry[0], entry[1], entry[2]);
      this._steerTo(_v, 0.35, false);
      if (!c.grounded && this._phaseTime >= (this._nextShot ?? 0)) {
        this._nextShot = this._phaseTime + 0.12;
        _v2.set(entry[0], entry[1], entry[2]);
        const res = this.rival.fire('a', _v2);
        if (res.ok && res.point.distanceTo(_v2) < 6) {
          this.phase = 'fall';
          this._phaseTime = 0;
          return;
        }
      }
      if (this._phaseTime > 12) this._finish('never_left');
      return;
    }

    if (this.phase === 'fall') {
      const opening = this.rival.portals.portal('rival', 'a');
      if (opening?.active) _v.copy(opening.group.position);
      else _v.set(entry[0], entry[1], entry[2]);
      this._steerTo(_v, 0.35, false);
      if (this._phaseTime > 16) this._finish('never_entered');
    }
  }

  _runFling(dt) {
    const from = this.surfaceById(this.current.from);
    const to = this.surfaceById(this.current.to);
    if (!from || !to) { this._finish('unknown_surface'); return; }
    const walk = this.current.kind === 'walk';

    if (this.phase === 'start') this.phase = 'aimFrom';

    if (this.phase === 'aimFrom' || this.phase === 'aimTo') {
      // Taking aim takes time on lower tiers; the pause reads as lining up
      // the shot, and it is most of what makes BOOT beatable by a human.
      if (this._phaseTime < this.skill.aimDelay) return;
      const surface = this.phase === 'aimFrom' ? from : to;
      const which = this.phase === 'aimFrom' ? 'a' : 'b';
      // A fling aims at the face's centre. A walk-through must open FLUSH
      // with the floor on both sides: a portal centred half way up a nine
      // metre wall is a window, and even a half-metre sill below the opening
      // is taller than the capsule radius, so the body cannot step over it.
      // Aiming at floor + half the portal height puts the bottom edge on the
      // ground, and the placement clamp stops it sinking any lower than the
      // panel allows.
      // A baked route may pin the portal off face-centre: on UPLINK the
      // pit portal must sit where the fall off the lip actually crosses
      // the floor, and the centre of that floor is 13 metres too deep.
      const pin = this.phase === 'aimFrom' ? this.current.aimFrom : this.current.aimTo;
      if (pin) _v2.set(pin[0], pin[1], pin[2]);
      else _v2.copy(surface.center);
      if (walk) {
        const floorY = this.phase === 'aimFrom'
          ? this.rival.position.y
          : (this.current.exitY ?? surface.collider.aabb.min.y);
        _v2.y = floorY + PORTAL_HALF_H - 0.05;
      }
      const res = this.rival.fire(which, _v2);
      if (res.ok && res.point.distanceTo(_v2) < Math.max(6, surface.halfU + surface.halfV)) {
        if (this.phase === 'aimFrom') {
          this.phase = 'aimTo';
        } else {
          this.phase = 'enter';
          // Snapshot the count now. Left undefined, the "have I gone through
          // yet" test below is true on the first frame and the agent skips
          // straight to coasting without ever entering the portal.
          this._travelMark = this.rival.controller.stats.portalTravels;
        }
        this._phaseTime = 0;
        this._retries = 0;
        return;
      }
      // No line of sight: close the gap and try again shortly.
      this._retries++;
      _v.copy(surface.center).addScaledVector(surface.normal, 3);
      this._steerTo(_v, 2.5);
      if (this._retries > 90 || this._phaseTime > 14 + this.skill.aimDelay) this._finish('no_line_of_sight');
      return;
    }

    if (this.phase === 'enter') {
      // Aim at the portal that actually got placed, not the middle of the
      // face: a wall can be sixty metres wide and the opening is 1.7.
      const opening = this.rival.portals.portal('rival', 'a');
      const mouth = opening?.active ? opening.group.position : from.center;
      // For a floor portal, walk to the spot just above the hole and gravity
      // does the rest. For a wall portal the target must be BEHIND the plane:
      // a point in front of it is a doorstep, and an agent that arrives at a
      // doorstep stops on it, satisfied, forever.
      const depth = from.kind === 'floor' ? 0.4 : -0.7;
      _v.copy(mouth).addScaledVector(from.normal, depth);
      const c = this.rival.controller;
      this._steerTo(_v, 0.35);
      if (c.stats.portalTravels !== this._travelMark) {
        this._travelMark = c.stats.portalTravels;
        this.phase = 'coast';
        this._phaseTime = 0;
      }
      if (this._phaseTime > 18) this._finish('never_entered');
      return;
    }

    if (this.phase === 'coast') {
      if (walk) {
        // A walk-through exits at strolling pace on purpose; grounded on the
        // far side simply means it worked.
        if (this.rival.controller.grounded || this._phaseTime > 3) this._finish();
        return;
      }
      // Mid-flight, drift toward wherever the plan goes next. Air control is
      // capped low, so this shades the landing rather than steering it, which
      // is what lands a vertical launch on a pad instead of back in the
      // portal it came out of.
      if (!this.rival.controller.grounded) {
        const next = this.goals[0];
        if (next?.kind === 'goto') {
          _v.set(next.target[0], next.target[1], next.target[2]);
          this._steerTo(_v, 2.0);
        }
      }
      if (this.rival.controller.grounded && this._phaseTime > 0.4) {
        // A fling that lands slow was walked into rather than fallen into:
        // the body dropped beside the entry portal, strolled in at ground
        // speed, and exited with nothing. The queued goals belong to the
        // route the launch was supposed to buy, so drop them and let the
        // planner set up the fling again from wherever we ended up.
        if (this.rival.speed < 18) {
          this.goals.length = 0;
          this._finish('slow_transit');
        } else {
          this._finish();
        }
      }
      if (this._phaseTime > 14) this._finish();
    }
  }

  /** Compact snapshot for the planner. Keep it small: it is sent every call. */
  describeState(playerPos, mode) {
    const p = this.rival.position;
    const round = (v) => Math.round(v * 10) / 10;
    const near = [...this.surfaces]
      .map((s) => ({ s, d: s.center.distanceTo(p) }))
      .sort((a, b) => a.d - b.d)
      .slice(0, 14)
      .map(({ s }) => ({
        id: s.id,
        kind: s.kind,
        at: [round(s.center.x), round(s.center.y), round(s.center.z)],
        facing: [round(s.normal.x), round(s.normal.y), round(s.normal.z)],
      }));

    return {
      mode,
      self: {
        at: [round(p.x), round(p.y), round(p.z)],
        speed: round(this.rival.speed),
        grounded: this.rival.controller.grounded,
      },
      player: playerPos
        ? { at: [round(playerPos.x), round(playerPos.y), round(playerPos.z)] }
        : null,
      objectives: this.targets
        .filter((t) => !(t.kind === 'shard' && t.taken))
        .slice(0, 8)
        .map((t) => ({
          kind: t.kind,
          at: [round(t.position.x), round(t.position.y), round(t.position.z)],
        })),
      surfaces: near,
      lastFailure: this.lastFailure,
    };
  }
}
